import { styled } from "@mui/system";
import { Tabs as MuiTabs, Tab as MuiTab } from "@mui/material";
import { tabClasses } from "@mui/material/Tab";
import { buttonClasses } from "@mui/material/Button";
import React from "react";

/**
 * Styled Tab component with custom theme styling
 * Uses MUI's Tab as the base component
 */
export const Tab = styled(MuiTab)(
  ({ theme }) => `
    font-family: "IBM Plex Sans", sans-serif;
    color: white;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 600;
    background-color: transparent;
    flex: 1;
    line-height: 1.2;
    padding: 10px 16px;
    margin: 0;
    border: none;
    border-radius: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    text-transform: none;
    min-height: 44px;
    max-height: 44px;
    transition: all 0.2s ease-in-out;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }

    &:focus {
      outline: none;
    }

    &.${tabClasses.selected} {
      background-color: #fff;
      color: ${theme.palette.primary.main};
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }

    &.${buttonClasses.disabled} {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `
);

/**
 * Styled Tabs container component
 * This replaces the old TabsList component
 * Use this as the direct parent of Tab components
 */
export const Tabs = styled(MuiTabs)(
  ({ theme }) => `
  width: 100%;
  max-width: 800px;
  background-color: ${theme.palette.primary.main};
  border-radius: 14px;
  margin: 0 auto 16px;
  padding: 6px;
  display: inline-flex;

  & .MuiTabs-flexContainer {
    display: flex;
    align-items: center;
    gap: 4px;
    height: 100%;
  }

  & .MuiTabs-indicator {
    display: none;
  }

  & .MuiTabs-scroller {
    overflow: visible !important;
  }

  box-shadow: 0 2px 8px ${
    theme.palette.mode === "dark" ? "rgba(0,0,0, 0.3)" : "rgba(0,0,0, 0.15)"
  };
  `
);

/**
 * TabPanel component for displaying tab content
 * Supports both value comparison (for string/number values) and index comparison
 *
 * Usage:
 *   <TabPanel value={activeTab} index={0}>Content</TabPanel>
 *   or
 *   <TabPanel value="tab1" index="tab1">Content</TabPanel>
 */
interface TabPanelProps {
  children?: React.ReactNode;
  value: any;
  index: any;
  [key: string]: any;
}

const BaseTabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
  // Convert both to strings for comparison to handle mixed string/number values
  const isActive = String(value) === String(index);

  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {isActive && children}
    </div>
  );
};

export const TabPanel = styled(BaseTabPanel)`
  width: 100%;
  font-family: "IBM Plex Sans", sans-serif;
  font-size: 0.875rem;
  padding: 24px 0;
  background-color: transparent;
  min-height: fit-content;

  &[hidden] {
    display: none;
  }

  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

/**
 * Legacy export for backward compatibility
 * @deprecated Use Tabs instead
 */
export const TabsList = Tabs;